import { useEffect, useState } from 'react';
import usePrevious from '../../../hooks/use-previous';
import { OnSelectChange, SelectItemProps } from '../../select/types';

export interface UseAutocompleteProps {
  value?: string | number | string[];
  onSearch?: (search: string) => void;
  onChange?: (e: OnSelectChange) => void;
  name?: string;
  list?: SelectItemProps[];
  search?: string;
  withTitles?: boolean;
}

export default function useAutocomplete({
  value = '',
  onSearch,
  onChange,
  name = '',
  search = '',
  list = [],
  withTitles,
}: UseAutocompleteProps) {
  const [searchQuery, setSearchQuery] = useState(search);
  const [isOptionsListVisible, setOptionsListVisible] = useState(false);

  const prevValue = usePrevious(value);

  const handleSearch = (e: { target: { value: string } }) => {
    setSearchQuery(e.target.value);

    if (onSearch) {
      onSearch(e.target.value);
    }

    if (!e.target.value && onChange) {
      onChange({
        target: {
          name,
          value: '',
        },
      });
    }
  };

  const onSelectChange = (e: OnSelectChange) => {
    setOptionsListVisible(false);

    if (e.target) {
      if (withTitles) {
        setSearchQuery(e.target.name);
      } else {
        setSearchQuery(
          list?.find(
            (item) => item.value.toString() === e.target.value.toString(),
          )?.name as string,
        );
      }
    }

    if (onChange) {
      const suffix = list?.find(
        (item) => item.value.toString() === e.target.value.toString(),
      )?.suffix as string;

      if (withTitles) {
        onChange({
          target: {
            name,
            value: e.target.value,
            suffix,
          },
        });
      } else {
        onChange({
          target: {
            ...e.target,
            suffix,
          },
        });
      }
    }
  };

  const handleReset = () => {
    setSearchQuery('');

    if (onChange) {
      onChange({
        target: {
          name,
          value: '',
        },
      });
    }

    if (onSearch) {
      onSearch('');
    }
  };

  useEffect(() => {
    if (value && !searchQuery) {
      setSearchQuery(search);
    } else if (prevValue && !value && searchQuery) {
      setSearchQuery('');
    }
  }, [value, searchQuery, prevValue, search]);

  return {
    isOptionsListVisible,
    setOptionsListVisible,
    searchQuery,
    handleSearch,
    onSelectChange,
    handleReset,
  };
}
